import React, { useState } from 'react';
import type { Page } from '../types';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '../lib/firebase';

interface CustomerAppPushPageProps {
  onNavigate?: (page: Page) => void;
}

/**
 * Thin POS composer that calls the customer-app project's sendTargetedNotification.
 * Requires VITE_CUSTOMER_APP_FUNCTIONS_REGION (optional) and that staff auth can invoke
 * the callable — typically via a shared admin secret proxy later. For now, documents
 * the payload and attempts the callable when Firebase is the customer-app project;
 * otherwise shows copy-paste instructions.
 */
const CustomerAppPushPage: React.FC<CustomerAppPushPageProps> = ({ onNavigate }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLink, setDeepLink] = useState('kingspark://home');
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      alert('Title and body are required.');
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const fn = getFirebaseFunctions();
      const call = httpsCallable(fn, 'sendCustomerAppNotification');
      await call({ title: title.trim(), body: body.trim(), deepLink: deepLink.trim() || undefined });
      setStatus('Queued via sendCustomerAppNotification (POS Functions bridge).');
      setTitle('');
      setBody('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus(
        `Could not send from POS Firebase (${msg}). ` +
          `Ensure functions sendCustomerAppNotification is deployed, or send from kingspark-app admin callable with staff claims.`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Customer app push</h1>
          <p className="text-sm text-gray-500">Replaces mobile admin-web notification composer. Delivery uses OneSignal on kingspark-app.</p>
        </div>
        {onNavigate && (
          <button type="button" className="text-sm text-pink-600" onClick={() => onNavigate('loyalty')}>
            ← Loyalty &amp; offers
          </button>
        )}
      </header>
      <main className="flex-1 p-6 max-w-xl space-y-4">
        <label className="block text-sm">
          <span className="text-gray-600">Title</span>
          <input className="mt-1 w-full border rounded-md px-3 py-2" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-gray-600">Body</span>
          <textarea className="mt-1 w-full border rounded-md px-3 py-2" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-gray-600">Deep link</span>
          <input className="mt-1 w-full border rounded-md px-3 py-2" value={deepLink} onChange={(e) => setDeepLink(e.target.value)} />
        </label>
        <button
          type="button"
          disabled={busy}
          onClick={() => void send()}
          className="bg-pink-500 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
        >
          {busy ? 'Sending…' : 'Send push'}
        </button>
        {status && <p className="text-sm text-gray-600 whitespace-pre-wrap">{status}</p>}
      </main>
    </div>
  );
};

export default CustomerAppPushPage;
